/**
 * Small multiples: a bar, line or pie tile repeated by the categories of a
 * variable, one panel per category (the first `MAX_PANELS`, in codebook
 * order). Each panel runs the tile's script with that category added to
 * its base as one more condition group. Pure.
 */

import type { HierarchicalDataEntry } from "types/engineOutput.types"
import { getBoolChildLabels } from "utils/codebookUtils"
import {
  DataTile,
  MAX_PANELS,
  RepeatBy,
  Tile,
} from "features/Dashboard/types/dashboardDocument"
import type { PanelRuntime } from "./dashboardStore"

/** The tiles that can repeat: their panels are mini charts. */
export type RepeatTile = Extract<DataTile, { type: "bar" | "line" | "pie" }>

export const canRepeat = (tile: Tile): tile is RepeatTile =>
  tile.type === "bar" || tile.type === "line" || tile.type === "pie"

/** The tile's `repeatBy` when it is a bar, line or pie tile. */
export const repeatOf = (tile: Tile): RepeatBy | undefined =>
  canRepeat(tile) ? tile.repeatBy : undefined

/** Every category of `variable` the codebook lists; Totals never is one. */
export const repeatCategories = (
  codebook: HierarchicalDataEntry[] | null,
  variable: string
): string[] => getBoolChildLabels(codebook, variable)

/** The categories that get a panel: the first `MAX_PANELS`, each once. */
export const panelCategories = (
  codebook: HierarchicalDataEntry[] | null,
  variable: string
): string[] =>
  [...new Set(repeatCategories(codebook, variable))].slice(0, MAX_PANELS)

/** The condition a panel adds to its tile's base: one group of one path. */
export const panelCondition = (variable: string, category: string): string[] => [
  `${variable}//${category}`,
]

/** A variable path as people read it: its last segment. */
export const variableLabel = (path: string): string =>
  path.split("//").pop() || path

/**
 * A repeated tile's panels in the order of its variable's categories, then
 * any panel the codebook no longer lists. Record keys alone would not do:
 * number-like categories ("2024") come first there, in numeric order.
 */
export const orderedPanels = (
  panels: Record<string, PanelRuntime>,
  codebook: HierarchicalDataEntry[] | null,
  variable: string | undefined
): { category: string; panel: PanelRuntime }[] => {
  const listed = variable ? repeatCategories(codebook, variable) : []
  const order = [
    ...listed.filter((c) => Object.hasOwn(panels, c)),
    ...Object.keys(panels).filter((c) => !listed.includes(c)),
  ]
  return order.map((category) => ({ category, panel: panels[category] }))
}
